import React, { useState, useEffect, useMemo } from 'react';
import { Banknote, Save, CreditCard, Smartphone, Copy } from 'lucide-react';
import WebPricingSection from './WebPricingSection';
import { API_BASE_URL } from '../../config';

const PAYMENT_OPTION_GROUPS = [
    { provider: 'freekassa', label: 'FreeKassa', methods: [{ id: 'card', label: 'Банковская карта' }, { id: 'sbp', label: 'СБП' }] },
    { provider: 'wata', label: 'Wata', methods: [{ id: 'card', label: 'Банковская карта' }, { id: 'sbp', label: 'СБП' }, { id: 'tpay', label: 'T-Pay' }] },
    { provider: 'wata_h2h', label: 'Wata H2H', methods: [{ id: 'card', label: 'Банковская карта' }, { id: 'sbp', label: 'СБП' }, { id: 'tpay', label: 'T-Pay' }] },
    { provider: 'liontech', label: 'LionTech', methods: [{ id: 'card', label: 'Банковская карта' }, { id: 'sbp', label: 'СБП' }] },
    { provider: 'platega', label: 'Platega', methods: [{ id: 'sbp', label: 'СБП' }] },
    { provider: 'pear', label: 'Pear (PayPear)', methods: [{ id: 'card', label: 'Банковская карта' }, { id: 'sbp', label: 'СБП' }] },
    { provider: 'aurapay', label: 'Aurapay', methods: [{ id: 'card', label: 'Банковская карта' }, { id: 'sbp', label: 'СБП' }] },
    { provider: 'paritypay', label: 'ParityPay', methods: [{ id: 'card', label: 'Банковская карта' }, { id: 'sbp', label: 'СБП' }] },
    { provider: 'lavepay', label: 'Lave Pay', methods: [{ id: 'wallet', label: 'Laveshka' }] },
];

const PAYMENT_OPTIONS = PAYMENT_OPTION_GROUPS.flatMap((group) =>
    group.methods.map((method) => `${group.provider}:${method.id}`)
);

const LAVEPAY_WEBHOOK_URL = 'https://tgstars.tech/api/payments/webhook/lavepay';

const PAYMENT_PROVIDER_TABS = PAYMENT_OPTION_GROUPS.map((group) => ({
    provider: group.provider,
    label: group.label,
}));

const legacyOptionsForProvider = (provider) => {
    const group = PAYMENT_OPTION_GROUPS.find((x) => x.provider === provider);
    return group ? group.methods.map((method) => `${provider}:${method.id}`) : ['freekassa:card', 'freekassa:sbp'];
};

const parsePaymentOptions = (raw, fallbackProvider) => {
    if (!raw) return legacyOptionsForProvider(fallbackProvider || 'freekassa');
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            const valid = parsed.filter((x) => PAYMENT_OPTIONS.includes(x));
            return valid.length ? valid : legacyOptionsForProvider(fallbackProvider || 'freekassa');
        }
    } catch (e) {
        const valid = String(raw)
            .split(',')
            .map((x) => x.trim())
            .filter((x) => PAYMENT_OPTIONS.includes(x));
        if (valid.length) return valid;
    }
    return legacyOptionsForProvider(fallbackProvider || 'freekassa');
};

const PricesPage = ({ onShowToast }) => {
    const [paymentProvider, setPaymentProvider] = useState('freekassa');
    const [activePaymentTab, setActivePaymentTab] = useState('freekassa');
    const [paymentOptionsEnabled, setPaymentOptionsEnabled] = useState(['freekassa:card', 'freekassa:sbp']);
    const [purchasePrice, setPurchasePrice] = useState(0);
    const [markupPercent, setMarkupPercent] = useState(0);
    const [finalPriceFreekassa, setFinalPriceFreekassa] = useState(null);
    const [finalPriceWata, setFinalPriceWata] = useState(null);
    const [finalPriceLiontech, setFinalPriceLiontech] = useState(null);
    const [finalPricePlatega, setFinalPricePlatega] = useState(null);
    const [finalPricePear, setFinalPricePear] = useState(null);
    const [finalPriceAurapay, setFinalPriceAurapay] = useState(null);
    const [finalPriceParitypay, setFinalPriceParitypay] = useState(null);
    const [finalPriceLavepay, setFinalPriceLavepay] = useState(null);
    const [referralBasePrice, setReferralBasePrice] = useState(1.33);
    const [commissionCard, setCommissionCard] = useState('1');
    const [commissionSbp, setCommissionSbp] = useState('7');
    const [tonkeeperDisabled, setTonkeeperDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // LionTech: креды (хранятся в БД; при refresh API новые токены тоже пишутся в БД)
    const [liontechAccessToken, setLiontechAccessToken] = useState('');
    const [liontechRefreshToken, setLiontechRefreshToken] = useState('');
    const [liontechSbpAccessToken, setLiontechSbpAccessToken] = useState('');
    const [liontechSbpRefreshToken, setLiontechSbpRefreshToken] = useState('');
    const [plategaMerchantId, setPlategaMerchantId] = useState('');
    const [plategaSecret, setPlategaSecret] = useState('');
    const [pearShopId, setPearShopId] = useState('');
    const [pearSecret, setPearSecret] = useState('');
    const [aurapayApiKey, setAurapayApiKey] = useState('');
    const [aurapayShopId, setAurapayShopId] = useState('');
    const [aurapaySecret2, setAurapaySecret2] = useState('');
    const [aurapayMaxAmountRub, setAurapayMaxAmountRub] = useState('2000');
    const [paritypayShopId, setParitypayShopId] = useState('');
    const [paritypaySecret1, setParitypaySecret1] = useState('');
    const [paritypaySecret2, setParitypaySecret2] = useState('');
    const [lavepayApiKey, setLavepayApiKey] = useState('');
    const [lavepayWebhookSecret, setLavepayWebhookSecret] = useState('');
    const [lavepayUsdRubRate, setLavepayUsdRubRate] = useState('100');
    const [lavepayInvoiceTtlSeconds, setLavepayInvoiceTtlSeconds] = useState('3600');
    const [manualPaymentReviewThresholdRub, setManualPaymentReviewThresholdRub] = useState('2000');
    const [wataH2HTerminalId, setWataH2HTerminalId] = useState('');
    const [wataH2HPublicId, setWataH2HPublicId] = useState('');
    const [wataH2HToken, setWataH2HToken] = useState('');
    const [wataDgToken, setWataDgToken] = useState('');
    const [telegramPremiumMarkupPercent, setTelegramPremiumMarkupPercent] = useState('0');
    /** Авто POST /confirm при webhook Review (WATA DG). По умолчанию вкл. */
    const [wataDgAutoConfirmReview, setWataDgAutoConfirmReview] = useState(true);

    const finalPrice = useMemo(() => {
        if (!purchasePrice || !markupPercent) return 0;
        return Number((purchasePrice * (1 + markupPercent / 100)).toFixed(2));
    }, [purchasePrice, markupPercent]);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [pricingRes, botRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/settings/pricing`, {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                    }),
                    fetch(`${API_BASE_URL}/api/settings/bot`, {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                    }),
                ]);
                if (pricingRes.ok) {
                    const data = await pricingRes.json();
                    setPurchasePrice(typeof data.purchase_price === 'number' ? data.purchase_price : parseFloat(data.purchase_price) || 0);
                    setMarkupPercent(typeof data.markup_percent === 'number' ? data.markup_percent : parseFloat(data.markup_percent) || 0);
                    setFinalPriceFreekassa(data.final_price_freekassa ?? null);
                    setFinalPriceWata(data.final_price_wata ?? null);
                    setFinalPriceLiontech(data.final_price_liontech ?? null);
                    setFinalPricePlatega(data.final_price_platega ?? null);
                    setFinalPricePear(data.final_price_pear ?? null);
                    setFinalPriceAurapay(data.final_price_aurapay ?? null);
                    setFinalPriceParitypay(data.final_price_paritypay ?? null);
                    setFinalPriceLavepay(data.final_price_lavepay ?? null);
                    setReferralBasePrice(
                        typeof data.referral_base_price === 'number'
                            ? data.referral_base_price
                            : parseFloat(data.referral_base_price) || 1.33
                    );
                }
                if (botRes.ok) {
                    const bot = await botRes.json();
                    const legacyProvider = bot.payment_provider || 'freekassa';
                    const parsedOptions = parsePaymentOptions(bot.payment_options_enabled, legacyProvider);
                    const firstProvider = parsedOptions[0]?.split(':')[0] || legacyProvider;
                    setPaymentProvider(legacyProvider);
                    setActivePaymentTab(
                        PAYMENT_PROVIDER_TABS.some((tab) => tab.provider === firstProvider)
                            ? firstProvider
                            : 'freekassa'
                    );
                    setPaymentOptionsEnabled(parsedOptions);
                    setCommissionCard(bot.commission_card ?? '1');
                    setCommissionSbp(bot.commission_sbp ?? '7');
                    const tkOff = bot.tonkeeper_disabled;
                    setTonkeeperDisabled(tkOff === '1' || tkOff === 'true' || tkOff === 'yes' || tkOff === 'on');
                    setLiontechAccessToken(bot.liontech_access_token ?? '');
                    setLiontechRefreshToken(bot.liontech_refresh_token ?? '');
                    setLiontechSbpAccessToken(bot.liontech_sbp_access_token ?? '');
                    setLiontechSbpRefreshToken(bot.liontech_sbp_refresh_token ?? '');
                    setPlategaMerchantId(bot.platega_merchant_id ?? '');
                    setPlategaSecret(bot.platega_secret ?? '');
                    setPearShopId(bot.pear_shop_id ?? '');
                    setPearSecret(bot.pear_secret ?? '');
                    setAurapayApiKey(bot.aurapay_api_key ?? '');
                    setAurapayShopId(bot.aurapay_shop_id ?? '');
                    setAurapaySecret2(bot.aurapay_secret2 ?? '');
                    setAurapayMaxAmountRub(bot.aurapay_max_amount_rub ?? '2000');
                    setParitypayShopId(bot.paritypay_shop_id ?? '');
                    setParitypaySecret1(bot.paritypay_secret1 ?? '');
                    setParitypaySecret2(bot.paritypay_secret2 ?? '');
                    setLavepayApiKey(bot.lavepay_api_key ?? '');
                    setLavepayWebhookSecret(bot.lavepay_webhook_secret ?? '');
                    setLavepayUsdRubRate(bot.lavepay_usd_rub_rate ?? '100');
                    setLavepayInvoiceTtlSeconds(bot.lavepay_invoice_ttl_seconds ?? '3600');
                    setManualPaymentReviewThresholdRub(
                        bot.manual_payment_review_threshold_rub ?? bot.manual_payment_review_threshold_stars ?? '2000'
                    );
                    setWataH2HTerminalId(bot.wata_h2h_terminal_id ?? '');
                    setWataH2HPublicId(bot.wata_h2h_public_id ?? '');
                    setWataH2HToken(bot.wata_h2h_token ?? '');
                    setWataDgToken(bot.wata_dg_token ?? '');
                    setTelegramPremiumMarkupPercent(
                        bot.telegram_premium_markup_percent != null && String(bot.telegram_premium_markup_percent).trim() !== ''
                            ? String(bot.telegram_premium_markup_percent)
                            : '0'
                    );
                    const ac = bot.wata_dg_auto_confirm_review;
                    if (ac === undefined || ac === null || ac === '') {
                        setWataDgAutoConfirmReview(true);
                    } else {
                        setWataDgAutoConfirmReview(
                            !(ac === '0' || ac === 'false' || ac === 'no' || ac === 'off')
                        );
                    }
                }
            } catch (e) {
                console.error('Error loading pricing/bot:', e);
                if (onShowToast) onShowToast('Не удалось загрузить настройки');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [onShowToast]);

    const togglePaymentOption = (optionId) => {
        const provider = optionId.split(':')[0];
        if (PAYMENT_PROVIDER_TABS.some((tab) => tab.provider === provider)) {
            setActivePaymentTab(provider);
        }
        setPaymentOptionsEnabled((current) => {
            const next = current.includes(optionId)
                ? current.filter((x) => x !== optionId)
                : [...current, optionId].filter((x) => PAYMENT_OPTIONS.includes(x));
            const firstProvider = next[0]?.split(':')[0];
            if (firstProvider) setPaymentProvider(firstProvider);
            return next;
        });
    };

    const enabledMethodsCount = (provider) =>
        paymentOptionsEnabled.filter((optionId) => optionId.startsWith(`${provider}:`)).length;

    const isActivePaymentTab = (...providers) => providers.includes(activePaymentTab);

    const paymentTabCardClass = (...providers) =>
        `${isActivePaymentTab(...providers) ? '' : 'hidden'} bg-white p-6 rounded-xl shadow-sm border border-slate-100`;

    const paymentTabBlockClass = (baseClass, ...providers) =>
        `${isActivePaymentTab(...providers) ? '' : 'hidden'} ${baseClass}`;

    const handleSave = async () => {
        if (purchasePrice <= 0) {
            if (onShowToast) onShowToast('Цена закупки должна быть больше нуля');
            return;
        }
        if (markupPercent < 0) {
            if (onShowToast) onShowToast('Процент надбавки не может быть отрицательным');
            return;
        }
        if (referralBasePrice <= 0) {
            if (onShowToast) onShowToast('Базовая цена для партнёрских ботов должна быть больше нуля');
            return;
        }
        if (!paymentOptionsEnabled.length) {
            if (onShowToast) onShowToast('Выберите хотя бы один способ оплаты');
            return;
        }
        try {
            setSaving(true);
            await fetch(`${API_BASE_URL}/api/settings/pricing`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    purchase_price: purchasePrice,
                    markup_percent: markupPercent,
                    ...(finalPriceFreekassa != null && finalPriceFreekassa > 0 && { final_price_freekassa: finalPriceFreekassa }),
                    ...(finalPriceWata != null && finalPriceWata > 0 && { final_price_wata: finalPriceWata }),
                    ...(finalPriceLiontech != null && finalPriceLiontech > 0 && { final_price_liontech: finalPriceLiontech }),
                    ...(finalPricePlatega != null && finalPricePlatega > 0 && { final_price_platega: finalPricePlatega }),
                    ...(finalPricePear != null && finalPricePear > 0 && { final_price_pear: finalPricePear }),
                    ...(finalPriceAurapay != null && finalPriceAurapay > 0 && { final_price_aurapay: finalPriceAurapay }),
                    ...(finalPriceParitypay != null && finalPriceParitypay > 0 && { final_price_paritypay: finalPriceParitypay }),
                    ...(finalPriceLavepay != null && finalPriceLavepay > 0 && { final_price_lavepay: finalPriceLavepay }),
                    referral_base_price: referralBasePrice,
                }),
            });
            await fetch(`${API_BASE_URL}/api/settings/bot`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    payment_provider: paymentProvider,
                    payment_options_enabled: JSON.stringify(paymentOptionsEnabled),
                    commission_card: String(commissionCard),
                    commission_sbp: String(commissionSbp),
                    tonkeeper_disabled: tonkeeperDisabled ? '1' : '0',
                    liontech_access_token: liontechAccessToken.trim(),
                    liontech_refresh_token: liontechRefreshToken.trim(),
                    liontech_sbp_access_token: liontechSbpAccessToken.trim(),
                    liontech_sbp_refresh_token: liontechSbpRefreshToken.trim(),
                    platega_merchant_id: plategaMerchantId.trim(),
                    platega_secret: plategaSecret.trim(),
                    pear_shop_id: pearShopId.trim(),
                    pear_secret: pearSecret.trim(),
                    aurapay_api_key: aurapayApiKey.trim(),
                    aurapay_shop_id: aurapayShopId.trim(),
                    aurapay_secret2: aurapaySecret2.trim(),
                    aurapay_max_amount_rub: String(aurapayMaxAmountRub || '2000').trim(),
                    paritypay_shop_id: paritypayShopId.trim(),
                    paritypay_secret1: paritypaySecret1.trim(),
                    paritypay_secret2: paritypaySecret2.trim(),
                    lavepay_api_key: lavepayApiKey.trim(),
                    lavepay_webhook_secret: lavepayWebhookSecret.trim(),
                    lavepay_usd_rub_rate: String(lavepayUsdRubRate || '100').trim(),
                    lavepay_invoice_ttl_seconds: String(lavepayInvoiceTtlSeconds || '3600').trim(),
                    manual_payment_review_threshold_rub: String(manualPaymentReviewThresholdRub || '2000').trim(),
                    wata_h2h_terminal_id: wataH2HTerminalId.trim(),
                    wata_h2h_public_id: wataH2HPublicId.trim(),
                    wata_h2h_token: wataH2HToken.trim(),
                    wata_dg_token: wataDgToken.trim(),
                    telegram_premium_markup_percent: String(telegramPremiumMarkupPercent ?? '0').trim(),
                    wata_dg_auto_confirm_review: wataDgAutoConfirmReview ? '1' : '0',
                }),
            });
            if (onShowToast) onShowToast('Настройки ценообразования сохранены');
        } catch (e) {
            console.error('Error saving pricing:', e);
            if (onShowToast) onShowToast(e.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const copyLavepayWebhookUrl = async () => {
        try {
            await navigator.clipboard.writeText(LAVEPAY_WEBHOOK_URL);
            onShowToast?.('Адрес webhook скопирован');
        } catch (error) {
            console.error('Failed to copy Lave Pay webhook URL:', error);
            onShowToast?.('Не удалось скопировать адрес');
        }
    };

    if (loading) {
        return (
            <div className="max-w-4xl space-y-6">
                <h2 className="text-2xl font-bold text-slate-800">Ценообразование</h2>
                <div className="flex items-center justify-center py-12 text-slate-500">Загрузка...</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-8 pb-10">
            <h2 className="text-2xl font-bold text-slate-800">Ценообразование</h2>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800">Способы оплаты в боте</h3>
                        <p className="text-sm text-slate-500">
                            Пользователь увидит все включенные варианты и сам выберет платежку перед созданием ссылки.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {PAYMENT_OPTION_GROUPS.map((group) => (
                        <div key={group.provider} className="border-t border-slate-100 pt-4">
                            <div className="text-sm font-semibold text-slate-800 mb-3">{group.label}</div>
                            <div className="space-y-2">
                                {group.methods.map((method) => {
                                    const optionId = `${group.provider}:${method.id}`;
                                    return (
                                        <label key={optionId} className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={paymentOptionsEnabled.includes(optionId)}
                                                onChange={() => togglePaymentOption(optionId)}
                                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                            />
                                            <span className="text-sm text-slate-700">{method.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <p className="text-xs text-slate-500 mt-4">
                    Технический fallback для старых заказов: <code className="bg-slate-100 px-1 rounded">{paymentProvider}</code>
                </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                        <Banknote size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800">Ручная проверка дорогих оплат</h3>
                        <p className="text-sm text-slate-500">
                            Если сумма оплаченного заказа Stars выше порога, звёзды выдаются только после подтверждения администратором.
                        </p>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Порог ручной проверки, ₽</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={manualPaymentReviewThresholdRub}
                        onChange={(e) => setManualPaymentReviewThresholdRub(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="2000"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                        Пустое или некорректное значение на backend считается как 2000 ₽.
                    </p>
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-slate-100 text-slate-600 rounded-lg">
                        <Smartphone size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800">Надбавки способов оплаты</h3>
                        <p className="text-sm text-slate-500">
                            Общие надбавки для внешних провайдеров: FreeKassa, LionTech, Platega, Pear и Aurapay.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <CreditCard size={16} className="text-slate-500" />
                            Надбавка банковская карта (%)
                        </label>
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={commissionCard}
                            onChange={(e) => setCommissionCard(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="1"
                        />
                        <p className="text-xs text-slate-500 mt-1">Добавляется к сумме при выборе «Банковская карта»</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <Smartphone size={16} className="text-slate-500" />
                            Надбавка СБП (%)
                        </label>
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={commissionSbp}
                            onChange={(e) => setCommissionSbp(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="7"
                        />
                        <p className="text-xs text-slate-500 mt-1">Добавляется к сумме при выборе «СБП»</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800">Настройки платежных систем</h3>
                        <p className="text-sm text-slate-500">
                            Выберите платежку, чтобы открыть только ее креды и цену за звезду.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2" role="tablist" aria-label="Платежные системы">
                    {PAYMENT_PROVIDER_TABS.map((tab) => {
                        const active = activePaymentTab === tab.provider;
                        const enabledCount = enabledMethodsCount(tab.provider);
                        return (
                            <button
                                key={tab.provider}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setActivePaymentTab(tab.provider)}
                                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2 ${
                                    active
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-700'
                                }`}
                            >
                                <span>{tab.label}</span>
                                <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                        active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                                    }`}
                                >
                                    {enabledCount}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* LionTech: креды — хранятся в БД; при истечении access приложение само обновляет их через refresh */}
            <div className={paymentTabCardClass('liontech')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">LionTech: доступ (креды)</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Задайте токены из кабинета LionTech. Сохраняются в БД; при истечении access приложение само обновит их через refresh и перезапишет здесь. Если пусто — используются значения из .env.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Access Token (Карты)</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={liontechAccessToken}
                            onChange={(e) => setLiontechAccessToken(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Refresh Token (Карты)</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={liontechRefreshToken}
                            onChange={(e) => setLiontechRefreshToken(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Access Token (СБП)</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={liontechSbpAccessToken}
                            onChange={(e) => setLiontechSbpAccessToken(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Необязательно; если пусто — используются креды карт"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Refresh Token (СБП)</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={liontechSbpRefreshToken}
                            onChange={(e) => setLiontechSbpRefreshToken(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Необязательно"
                        />
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('platega')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-sky-100 text-sky-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Platega: доступ (креды)</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Merchant ID и API key для Platega. Если поля пустые, backend использует значения из{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">PLATEGA_MERCHANT_ID</code> и{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">PLATEGA_SECRET</code>.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Merchant ID</label>
                        <input
                            type="text"
                            autoComplete="off"
                            value={plategaMerchantId}
                            onChange={(e) => setPlategaMerchantId(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="UUID мерчанта"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">API key / Secret</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={plategaSecret}
                            onChange={(e) => setPlategaSecret(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('pear')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Pear (PayPear): доступ (креды)</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    <strong>ID магазина</strong> (Настройки → Магазин) и <strong>секретный ключ</strong> (Интеграция → Ключи API), как в{' '}
                    <a href="https://paypear.ru/docs/" target="_blank" rel="noreferrer" className="text-indigo-600 underline">
                        документации Pear
                    </a>
                    . Если поля пустые, backend использует <code className="text-xs bg-slate-100 px-1 rounded">PEAR_SHOP_ID</code> и{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">PEAR_SECRET</code>. Для вебхуков укажите публичный{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">BACKEND_PUBLIC_URL</code> (HTTPS) — иначе webhook_url в создании платежа не передаётся.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">ID магазина</label>
                        <input
                            type="text"
                            autoComplete="off"
                            value={pearShopId}
                            onChange={(e) => setPearShopId(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Из личного кабинета Pear"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Секретный ключ API</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={pearSecret}
                            onChange={(e) => setPearSecret(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('aurapay')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-cyan-100 text-cyan-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Aurapay: доступ (креды)</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Для API нужны <strong>API key</strong>, <strong>Shop ID</strong> и <strong>Secret key #2</strong> из настроек кассы.
                    Если поля пустые, backend использует <code className="text-xs bg-slate-100 px-1 rounded">AURAPAY_API_KEY</code>,{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">AURAPAY_SHOP_ID</code> и{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">AURAPAY_SECRET2</code>. Callback URL публичный:
                    <code className="text-xs bg-slate-100 px-1 rounded ml-1">https://domain/api/payments/webhook/aurapay</code>.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">API key</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={aurapayApiKey}
                            onChange={(e) => setAurapayApiKey(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Shop ID</label>
                        <input
                            type="text"
                            autoComplete="off"
                            value={aurapayShopId}
                            onChange={(e) => setAurapayShopId(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="UUID кассы"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Secret key #2 для webhook</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={aurapaySecret2}
                            onChange={(e) => setAurapaySecret2(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Максимальная сумма платежа Aurapay, ₽</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={aurapayMaxAmountRub}
                            onChange={(e) => setAurapayMaxAmountRub(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="2000"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                            Если итоговая сумма заказа выше этого значения, Aurapay скрывается в боте. Пустое или некорректное значение = 2000 ₽.
                        </p>
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('paritypay')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">ParityPay: доступ (креды)</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Для API нужны <strong>Shop ID</strong>, <strong>Secret key #1</strong> (подпись запросов) и <strong>Secret key #2</strong> (webhook).
                    Если поля пустые, backend использует <code className="text-xs bg-slate-100 px-1 rounded">PARITYPAY_SHOP_ID</code>,{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">PARITYPAY_SECRET1</code> и{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">PARITYPAY_SECRET2</code>. Callback URL публичный:
                    <code className="text-xs bg-slate-100 px-1 rounded ml-1">https://domain/api/payments/webhook/paritypay</code>.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Shop ID</label>
                        <input
                            type="text"
                            autoComplete="off"
                            value={paritypayShopId}
                            onChange={(e) => setParitypayShopId(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="UUID кассы"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Secret key #1 (API)</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={paritypaySecret1}
                            onChange={(e) => setParitypaySecret1(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Secret key #2 (webhook)</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={paritypaySecret2}
                            onChange={(e) => setParitypaySecret2(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env"
                        />
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('lavepay')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-amber-100 text-amber-700 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Lave Pay: доступ и конвертация</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    API key создаётся в Laveshka и показывается один раз. Webhook secret берётся отдельно в настройках мерчанта.
                    Не включайте способ оплаты, пока оба секрета и HTTPS webhook не настроены.
                </p>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">HTTPS-адрес webhook</label>
                    <div className="flex items-stretch gap-2">
                        <input
                            type="text"
                            readOnly
                            value={LAVEPAY_WEBHOOK_URL}
                            className="min-w-0 flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-mono text-sm"
                        />
                        <button
                            type="button"
                            onClick={copyLavepayWebhookUrl}
                            className="w-12 shrink-0 inline-flex items-center justify-center border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50"
                            title="Скопировать адрес webhook"
                            aria-label="Скопировать адрес webhook"
                        >
                            <Copy size={18} />
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">API key</label>
                        <input
                            type="password"
                            autoComplete="new-password"
                            value={lavepayApiKey}
                            onChange={(e) => setLavepayApiKey(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="LAVE-..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Webhook secret</label>
                        <input
                            type="password"
                            autoComplete="new-password"
                            value={lavepayWebhookSecret}
                            onChange={(e) => setLavepayWebhookSecret(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="whsec_..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Курс USD/RUB</label>
                        <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={lavepayUsdRubRate}
                            onChange={(e) => setLavepayUsdRubRate(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="100"
                        />
                        <p className="text-xs text-slate-500 mt-1">Lave Pay принимает сумму счёта в USD; заказ и отчёты остаются в рублях.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Срок счёта, секунд</label>
                        <input
                            type="number"
                            min="60"
                            max="604800"
                            step="60"
                            value={lavepayInvoiceTtlSeconds}
                            onChange={(e) => setLavepayInvoiceTtlSeconds(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="3600"
                        />
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('wata_h2h')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Wata H2H: доступ (креды)</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Можно задать креды для H2H прямо здесь. Приоритет у БД; если поля пустые — используются значения из .env.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Terminal ID</label>
                        <input
                            type="text"
                            value={wataH2HTerminalId}
                            onChange={(e) => setWataH2HTerminalId(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="UUID терминала"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Public ID</label>
                        <input
                            type="text"
                            value={wataH2HPublicId}
                            onChange={(e) => setWataH2HPublicId(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="UUID public id"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">H2H Token (JWT)</label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={wataH2HToken}
                            onChange={(e) => setWataH2HToken(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="Оставьте пустым, чтобы использовать .env WATA_API_KEY"
                        />
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('wata')}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Wata Digital Goods (старый): API token</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Токен для API Digital Goods (dg-api.wata.pro: ваучеры, Telegram Premium TopUp). Если поле пустое и
                    сохранено пустым — берётся из окружения: сначала{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">WATA_DG_TOKEN</code>, затем{' '}
                    <code className="text-xs bg-slate-100 px-1 rounded">WATA_API_KEY</code>.
                </p>
                <input
                    type="password"
                    autoComplete="off"
                    value={wataDgToken}
                    onChange={(e) => setWataDgToken(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-mono text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="Пусто = из .env (WATA_DG_TOKEN или WATA_API_KEY)"
                />
                <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Telegram Premium — надбавка к цене WATA, %
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                        К minPrice из WATA TopUp (игра 185). Итоговая цена в боте: minPrice × (1 + % / 100), не выше
                        minPrice × 1.5 (лимит WATA).
                    </p>
                    <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={telegramPremiumMarkupPercent}
                        onChange={(e) => setTelegramPremiumMarkupPercent(e.target.value)}
                        className="w-full max-w-xs px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="0"
                    />
                </div>
            </div>

            <div className={paymentTabBlockClass('flex flex-col gap-1 py-2 border-t border-slate-100 pt-4', 'wata')}>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={wataDgAutoConfirmReview}
                        onChange={(e) => setWataDgAutoConfirmReview(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-slate-700">
                        WATA Digital Goods: автоматически подтверждать заказы в статусе Review
                    </span>
                </label>
                <span className="text-xs text-slate-500 pl-7">
                    Если WATA помечает оплату как «повышенный риск», заказ висит в Review до POST /confirm. Включено по
                    умолчанию — звёзды уйдут без ручного нажатия в мерчантке (риск мошенничества на вашей стороне).
                    Выкл. в .env: <code className="bg-slate-100 px-1 rounded">WATA_DG_AUTO_CONFIRM_REVIEW=0</code>
                </span>
            </div>

            {/* Переключатель Tonkeeper */}
            <div className="flex items-center gap-3 py-2">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={tonkeeperDisabled}
                        onChange={(e) => setTonkeeperDisabled(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Отключить Tonkeeper для WATA DG</span>
                </label>
                <span className="text-xs text-slate-500">
                    Только для обычной WATA (Digital Goods): звёзды отдаёт сама WATA, без Tonkeeper.
                    Wata H2H и остальные платёжки всегда отправляют через Tonkeeper.
                </span>
            </div>

            {/* Базовое ценообразование: цена закупки, общая надбавка %, итоговая цена */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                        <Banknote size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Базовое ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Базовая цена закупки и общий процент надбавки. Итоговая цена за звезду считается автоматически.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена закупки (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={purchasePrice || ''}
                            onChange={(e) => setPurchasePrice(Number(e.target.value) || 0)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="0.00"
                        />
                        <p className="text-xs text-slate-500 mt-1">Цена закупки звезд (Wata)</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Процент надбавки (%)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={markupPercent || ''}
                            onChange={(e) => setMarkupPercent(Number(e.target.value) || 0)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder="0.00"
                        />
                        <p className="text-xs text-slate-500 mt-1">Общая надбавка для Wata</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Итоговая цена за звезду (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={finalPrice}
                            readOnly
                            className="w-full px-4 py-3 bg-indigo-50 border-2 border-indigo-200 rounded-xl font-bold text-indigo-800"
                            placeholder="0.00"
                        />
                        <p className="text-xs text-slate-500 mt-1">Рассчитывается автоматически</p>
                    </div>
                </div>
                {isActivePaymentTab('wata', 'wata_h2h') && (
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду Wata (₽) — переопределение</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPriceWata ?? ''}
                            onChange={(e) => setFinalPriceWata(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full max-w-xs px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                        <p className="text-xs text-slate-500 mt-1">Если задано — в боте для Wata и Wata H2H используется эта цена вместо расчётной</p>
                    </div>
                )}
                <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Базовая цена для партнёрских ботов (₽)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={referralBasePrice}
                        onChange={(e) => setReferralBasePrice(Number(e.target.value) || 0)}
                        className="w-full max-w-xs px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="1.33"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                        Эта цена используется в формуле всех партнёрских ботов; их личная наценка добавляется поверх неё.
                    </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
                    <p className="text-sm text-slate-600">
                        <strong>Формула:</strong> Итоговая = Цена закупки × (1 + Процент надбавки / 100)
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                        <strong>Партнёрский бот:</strong> Цена = Базовая партнёрская цена × (1 + наценка партнёра/100 + надбавка способа оплаты/100)
                    </p>
                </div>
            </div>

            {/* FreeKassa ценообразование: цена за звезду */}
            <div className={paymentTabCardClass('freekassa')}>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">FreeKassa ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Цена за звезду для FreeKassa. Надбавки карта/СБП берутся из общих настроек выше.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду FreeKassa (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPriceFreekassa ?? ''}
                            onChange={(e) => setFinalPriceFreekassa(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                    </div>
                </div>
            </div>

            {/* LionTech ценообразование: отдельный блок */}
            <div className={paymentTabCardClass('liontech')}>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">LionTech ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Цена за звезду для платёжной системы LionTech. Надбавки карта/СБП берутся из общих настроек выше.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду LionTech (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPriceLiontech ?? ''}
                            onChange={(e) => setFinalPriceLiontech(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                        <p className="text-xs text-slate-500 mt-1">Если задано — в боте для LionTech используется эта цена вместо расчётной</p>
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('platega')}>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-sky-100 text-sky-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Platega ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Цена за звезду для платёжной системы Platega. Надбавки карта/СБП берутся из общих настроек выше.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду Platega (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPricePlatega ?? ''}
                            onChange={(e) => setFinalPricePlatega(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                        <p className="text-xs text-slate-500 mt-1">Если задано — используется для ручной ссылки и как цена провайдера по аналогии с остальными платёжками</p>
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('pear')}>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Pear ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Цена за звезду для Pear. Надбавки карта/СБП — из общих настроек выше.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду Pear (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPricePear ?? ''}
                            onChange={(e) => setFinalPricePear(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                        <p className="text-xs text-slate-500 mt-1">Если задано — в боте для Pear используется эта цена вместо расчётной</p>
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('aurapay')}>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-cyan-100 text-cyan-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Aurapay ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Цена за звезду для Aurapay. Надбавки карта/СБП берутся из общих настроек выше.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду Aurapay (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPriceAurapay ?? ''}
                            onChange={(e) => setFinalPriceAurapay(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                        <p className="text-xs text-slate-500 mt-1">Если задано — в боте для Aurapay используется эта цена вместо расчётной</p>
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('paritypay')}>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">ParityPay ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Цена за звезду для ParityPay. Надбавки карта/СБП берутся из общих настроек выше.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду ParityPay (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPriceParitypay ?? ''}
                            onChange={(e) => setFinalPriceParitypay(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                        <p className="text-xs text-slate-500 mt-1">Если задано — в боте для ParityPay используется эта цена вместо расчётной</p>
                    </div>
                </div>
            </div>

            <div className={paymentTabCardClass('lavepay')}>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-amber-100 text-amber-700 rounded-lg">
                        <CreditCard size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Lave Pay ценообразование</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Цена Stars в рублях до конвертации счёта в USD. Комиссия Lave Pay должна быть учтена в этой цене.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Цена за звезду Lave Pay (₽)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={finalPriceLavepay ?? ''}
                            onChange={(e) => setFinalPriceLavepay(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                            placeholder={String(finalPrice)}
                        />
                        <p className="text-xs text-slate-500 mt-1">Если не задано, используется общая расчётная цена.</p>
                    </div>
                </div>
            </div>

            <button
                onClick={handleSave}
                disabled={saving || !purchasePrice || !markupPercent}
                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-medium hover:bg-indigo-700 transition-all flex justify-center items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Save size={18} /> {saving ? 'Сохранение…' : 'Сохранить изменения'}
            </button>

            <WebPricingSection onShowToast={onShowToast} />
        </div>
    );
};

export default PricesPage;
